import {ApolloLink, Observable, Operation, ApolloClient, InMemoryCache, createHttpLink} from "@apollo/client";
import {onError} from "@apollo/client/link/error";
import {fromPromise} from "@apollo/client/link/utils";
import {LOCAL_STORAGE_KEYS} from "../common/constants";
import {getAppTokenHeaders} from "./appTokenHeaders";
import {RefreshTokenDocument} from "./generated/hooks";

// Get endpoint from the environment
const endpoint =
    process.env.REACT_APP_GRAPHQL_ENDPOINT ||
    `https://api.stg.robinfood.dev`;

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let pendingRequests: Array<() => void> = [];

// Create a standalone Apollo Client for refresh operations (without error link to avoid circular dependency)
const createRefreshClient = () => {
    return new ApolloClient({
        link: createHttpLink({
            uri: `${endpoint}/graphql`,
            headers: getAppTokenHeaders(),
        }),
        cache: new InMemoryCache(),
    });
};

// Function to set the authorization header
const setAuthorizationHeader = (operation: Operation, token: string | null) => {
    operation.setContext(({headers = {}}: { headers?: Record<string, string> }) => ({
        headers: {
            ...headers,
            ...getAppTokenHeaders(),
            authorization: token ? `Bearer ${token}` : "",
        },
    }));
};


// Function to refresh the access token using Apollo Client
const refreshAccessToken = async (): Promise<string | null> => {
    try {
        const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);

        if (!refreshToken) {
            console.warn("No refresh token available");
            return null;
        }

        // Create a dedicated client for refresh operation
        const refreshClient = createRefreshClient();

        // Call the refresh token mutation using Apollo Client
        const result = await refreshClient.mutate({
            mutation: RefreshTokenDocument,
            variables: {
                refreshToken,
            },
        });

        if (!result.data?.refreshToken) {
            console.error("Refresh token failed: No data returned");
            return null;
        }

        const {accessToken, refreshToken: newRefreshToken} = result.data.refreshToken;

        // Update tokens in localStorage
        localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, accessToken);
        if (newRefreshToken) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
        }

        return accessToken;
    } catch (error) {
        console.error("Error refreshing token:", error);
        return null;
    }
};

// Function to handle logout
const handleLogout = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    window.location.href = "/login";
};

// Development utility function to test token refresh (only available in development)
export const testTokenRefresh = () => {
    if (process.env.NODE_ENV === 'development') {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    } else {
        console.warn("testTokenRefresh is only available in development mode");
    }
};

// Development utility to simulate expired token by setting an invalid token
export const simulateExpiredToken = () => {
    if (process.env.NODE_ENV === 'development') {
        localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, "expired_token_simulation");
    } else {
        console.warn("simulateExpiredToken is only available in development mode");
    }
};

// Development utility to simulate forbidden error by setting a token that will cause 403
export const simulateForbiddenError = () => {
    if (process.env.NODE_ENV === 'development') {
        localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, "forbidden_token_simulation");
    } else {
        console.warn("simulateForbiddenError is only available in development mode");
    }
};

// Development utility to check current token status
export const checkTokenStatus = () => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    
    return { accessToken: !!accessToken, refreshToken: !!refreshToken };
};

// Process pending requests after token refresh
const resolvePendingRequests = () => {
    pendingRequests.forEach((callback) => callback());
    pendingRequests = [];
};

// Create the error link that handles token refresh
export const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
    if (graphQLErrors) {
        // First, check if ANY error is an auth error
        const hasAuthError = graphQLErrors.some(err =>
            err.extensions?.code === "UNAUTHENTICATED" ||
            err.extensions?.code === "UNAUTHORIZED_TOKEN_VALIDATION" ||
            err.extensions?.code === "FORBIDDEN" ||
            err.message?.includes("Unauthorized") ||
            err.message?.includes("jwt expired") ||
            err.message?.includes("invalid token") ||
            err.message?.includes("User validation failed") ||
            err.message?.includes("Forbidden resource")
        );

        // If there's an auth error, handle token refresh FIRST
        if (hasAuthError) {
            // Don't retry for login or refresh mutations
            if (
                operation.operationName === "PortalLogin" ||
                operation.operationName === "RefreshToken"
            ) {
                return;
            }

            // If already refreshing, queue this request
            if (isRefreshing) {
                return fromPromise(
                    new Promise<void>((resolve) => {
                        pendingRequests.push(() => resolve());
                    })
                ).flatMap(() => {
                    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
                    setAuthorizationHeader(operation, token);
                    return forward(operation);
                });
            }

            // Set refreshing flag
            isRefreshing = true;

            return fromPromise(
                refreshAccessToken()
                    .then((newToken) => {
                        if (!newToken) {
                            console.error(`❌ Token refresh failed for operation ${operation.operationName} - redirecting to login`);
                            handleLogout();
                            return null;
                        }

                        // Update the operation with the new token
                        setAuthorizationHeader(operation, newToken);

                        // Resolve all pending requests
                        resolvePendingRequests();

                        return newToken;
                    })
                    .catch((error) => {
                        console.error(`❌ Token refresh promise rejected for operation ${operation.operationName}:`, error);
                        handleLogout();
                        return null;
                    })
                    .finally(() => {
                        isRefreshing = false;
                    })
            ).flatMap((token) => {
                if (!token) {
                    // Token refresh failed, stop here
                    return Observable.of();
                }
                // Retry the failed request with new token
                return forward(operation);
            });
        }
    }

    if (networkError) {
        console.error(`[Network error]: ${networkError}`);
    }
});

// Create the auth link that adds the authorization header to all requests
export const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    setAuthorizationHeader(operation, token);
    return forward(operation);
});
